import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '@app/feature/authentication/store/auth.store';
import {
  isPendingProfessionalRole,
  isProfessionalRole,
} from '@core/utils/auth.util';
import { AuthenticationService } from '@app/feature/authentication/service/authentication.service';
import { safeGet, safeJsonParse } from '@core/utils/safe-storage.util';
import { catchError, map, of } from 'rxjs';

/**
 * Garde d'accès à l'espace professionnel (gestion des restaurants).
 *
 * - Utilisateur non authentifié sans session → redirige vers /authentication/login avec returnUrl.
 * - Token expiré mais cookie de session présent → tente un refresh silencieux avant décision.
 * - Utilisateur authentifié avec un rôle professionnel (ADMIN, OWNER, MANAGER, STAFF) → accès autorisé.
 * - Utilisateur en attente d'approbation (MANAGER_PENDING) → redirige vers /management/pending.
 * - Autre rôle (CUSTOMER, COURIER...) → accès refusé.
 */
export const ManagerGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authStore = inject(AuthStore);
  const authService = inject(AuthenticationService);

  const checkRole = (role?: string | null): boolean | string => {
    if (isProfessionalRole(role)) {
      return true;
    }
    if (isPendingProfessionalRole(role)) {
      return '/management/pending';
    }
    return '/error/forbidden';
  };

  const getRoleFromSources = (respUser?: unknown): string | null => {
    // Le claim JWT (signé serveur) prime sur le store et le stockage local
    // (réinscriptible depuis le navigateur) pour la répartition des espaces.
    const claim = authService.getRole();
    if (claim) return claim;
    const user = authStore.user();
    const raw = safeGet('user');
    const storedUser = safeJsonParse<{ role?: string | null } | null>(
      raw,
      null,
    );
    const fromResp = (respUser as { role?: string | null } | null)?.role;
    const userData = (respUser as object) ?? user ?? storedUser;
    return (
      fromResp ?? (userData as { role?: string | null } | null)?.role ?? null
    );
  };

  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    // Rendu serveur : pas de cookie, pas de refresh ; décision synchrone sûre.
    if (!authService.isTokenValid()) {
      return router.createUrlTree(['/authentication/login']);
    }
    try {
      const role = getRoleFromSources();
      const result = checkRole(role);
      return result === true ? true : router.createUrlTree([result as string]);
    } catch {
      return router.createUrlTree(['/authentication/login']);
    }
  }

  if (authService.isTokenValid()) {
    try {
      const role = getRoleFromSources();
      const result = checkRole(role);
      if (result === true) {
        return true;
      }
      router.navigate([result as string]);
      return false;
    } catch {
      router.navigate(['/authentication/login'], {
        queryParams: { returnUrl: state.url },
      });
      return false;
    }
  }

  if (!authService.hasSessionHint()) {
    router.navigate(['/authentication/login'], {
      queryParams: { returnUrl: state.url },
    });
    return false;
  }

  return authService.refreshToken().pipe(
    map((response) => {
      if (!authService.isTokenValid(response.accessToken) || !response.user) {
        // Réponse de refresh incomplète : ne jamais réutiliser un rôle périmé.
        authService.clearLocalSession();
        router.navigate(['/authentication/login'], {
          queryParams: { returnUrl: state.url },
        });
        return false;
      }
      const role = getRoleFromSources(response.user);
      const result = checkRole(role);
      if (result === true) {
        return true;
      }
      router.navigate([result as string]);
      return false;
    }),
    catchError(() => {
      authService.clearLocalSession();
      router.navigate(['/authentication/login'], {
        queryParams: { returnUrl: state.url },
      });
      return of(false);
    }),
  );
};
